import { Option, Pagination as TPagination, Review as TReview, Supplier, Translation } from 'src/types';
import Review from 'src/components/Review';
import useQuery from 'src/hooks/useQuery';
import { AppRoutes, ApiRoutes } from 'src/lib/routes';
import { useMemo, useRef, useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import classNames from 'classnames';
import { FiX, FiChevronDown } from 'react-icons/fi';
import LightBox from 'src/components/LightBox';
import style from 'src/styles/modules/components/review_section_v2.module.scss';
import { flatten } from 'lodash-es';
import { BsStarFill } from 'react-icons/bs';
import ReadMore from './ReadMore';
import Link from 'next/link';
import ImageWithFallback from './ImageWithFallback';
import useBreakpoints from 'src/hooks/useBreakpoints';
import { Overlay, Popover } from 'react-bootstrap';
import { useLocale, useTranslations } from 'next-intl';

const REVIEW_SORT =  [
  { value: 'is_option_review desc,rating desc,body_length desc', translationKey: 'highestLowest' },
  { value: 'rating asc,body_length desc', translationKey: 'lowestHighest' },
  { value: 'review_date desc', translationKey: 'newest' },
];

type Props = {
  supplier: Supplier;
  optionReviewsCount: number;
  option?: Option;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
};

type QueryData = { data: TReview[]; pagination: TPagination };

export const DEFAULT_SORT = 'is_option_review desc,rating desc,body_length desc';

const ReviewSectionV2: React.FC<Props> = ({
  optionReviewsCount,
  option,
  supplier,
  showModal,
  setShowModal,
}) => {
  const locale = useLocale();
  const translation = useTranslations('components.ReviewSectionV2')
  const { isScreenMd } = useBreakpoints();
  const [showSortTypeSelector, setShowSortTypeSelector] = useState(false);
  const selectorRef = useRef<HTMLDivElement>(null);
  const maxLength = isScreenMd ? 120 : 140;
  const containerRef = useRef<HTMLDivElement>(null);
  const [showMoreReview, setShowMoreReview] = useState<TReview>();
  const [showLightBox, setShowLightBox] = useState(false);
  const [reviewSort, setReviewSort] = useState(DEFAULT_SORT);
  const defaultTab = optionReviewsCount > 0 ? 'option' : 'supplier';
  const [reviewTab, setReviewTab] = useState(defaultTab);
  const [reviews, setReviews] = useState<TReview[]>([]);
  const [page, setPage] = useState(1);
  const [slideId, setSlideId] = useState<string>();
  const { data, isLoading } = useQuery<QueryData>(
    ApiRoutes.apiReviewsRoute(
      {},
      {
        reviewable_id: supplier.id,
        reviewable_type: 'Supplier',
        option_id: option?.id || 0,
        page: 1,
        per_page: 3,
        sorts: DEFAULT_SORT.split(','),
      }
    ).toUrl()
  );
  const lightBoxItems = useMemo(
    () =>
      flatten(reviews.map((review) => review.images)).map((image) => ({
        id: `review-lightbox-${image.id}`,
        imageUrl: image.webpMdUrl,
        smImageUrl: image.webpSmUrl,
      })),
    [reviews.map((v) => v.id).join('')]
  );

  const { data: initialReviews, pagination } = (data || {}) as QueryData;

  const { data: reviewsData, isLoading: isLoadingReviews } = useQuery<QueryData>(
    ApiRoutes.apiReviewsRoute(
      {},
      {
        reviewable_id: option && reviewTab === 'option' ? option.id : supplier.id,
        reviewable_type: option && reviewTab === 'option' ? 'SupplierActivityOption' : 'Supplier',
        page: page,
        option_id: option?.id || 0,
        per_page: 10,
        sorts: reviewSort.split(','),
      }
    ).toUrl()
  );

  const { data: fetchedReviews, pagination: reviewsPagination } = reviewsData || {};

  useEffect(() => {
    if (
      showMoreReview &&
      showModal &&
      reviews &&
      reviews.length > 0 &&
      containerRef.current &&
      reviewTab === (option && showMoreReview.option?.id === option.id ? 'option' : 'supplier')
    ) {
      const container = containerRef.current;
      const reviewItem = containerRef.current.querySelector(
        `#review-${showMoreReview.id}`
      ) as HTMLDivElement;
      if (reviewItem) {
        const scrollToReview = () => {
          container.scrollTo({
            left: 0,
            top: reviewItem.offsetTop,
            behavior: 'smooth',
          });
          setShowMoreReview(undefined);
        };
        const timer = window.setTimeout(scrollToReview, 1);

        return () => {
          window.clearTimeout(timer);
        };
      }
    }
  }, [showMoreReview?.id, showModal, reviews?.length, reviewTab]);

  useEffect(() => {
    if (showMoreReview?.id) {
      setPage(1);
      setReviewSort(DEFAULT_SORT);
      setReviewTab(option && showMoreReview.option?.id === option.id ? 'option' : 'supplier');
      setShowModal(true);
    }
  }, [option?.id, showMoreReview?.id]);

  useEffect(() => {
    const ele = containerRef.current;
    const nextPage = reviewsPagination?.nextPage;
    if (showModal && reviews && reviews.length > 0 && ele && nextPage) {
      let isLoading = false;
      const loadMore = () => {
        if (!isLoading && ele.scrollTop >= ele.scrollHeight / 2) {
          isLoading = true;
          setPage(nextPage);
        }
      };
      ele.addEventListener('scroll', loadMore);

      return () => {
        ele.removeEventListener('scroll', loadMore);
      };
    }
  }, [showModal, reviews?.length, reviewsPagination?.nextPage]);

  useEffect(() => {
    if (fetchedReviews) {
      if (page === 1) {
        setReviews(fetchedReviews);
      } else {
        setReviews((prev) => [...prev, ...fetchedReviews]);
      }
    }
  }, [fetchedReviews?.map((o) => o.id).join(','), page]);

  return isLoading ? (
    <></>
  ) : initialReviews && initialReviews.length > 0 ? (
    <>
      <div className="tw-bg-brand-navy">
        <div className="container tw-py-16 md:tw-py-24">
          <h2 className="tw-mb-0 tw-text-brand-green tw-font-styleSans tw-text-center tw-font-semibold tw-text-5xl md:tw-text-6xl md:tw-leading-none tw-leading-none tw-max-w-xs tw-mx-auto md:tw-max-w-none tw-px-2.5 md:tw-px-0">
            {translation('whatOtherGuestsSay')}
          </h2>

          <div className="tw-flex tw-items-center tw-justify-center tw-text-white tw-my-8 tw-leading-none">
            <div className="tw-font-semibold tw-mr-1">
              <span className="tw-leading-none">{supplier.reviewRating}</span>
            </div>
            <BsStarFill size={16} className="tw-text-orange tw-align-middle" />
            <div className="tw-text-white tw-mx-2">|</div>
            <div className="tw-underline tw-cursor-pointer" onClick={() => setShowModal(true)}>
              {translation('pluralReview', {count: supplier.ratedReviewsCount})}
            </div>
          </div>

          <div className="row">
            {initialReviews.map((review, index) => (
              <div
                className={classNames('col-md-4', { 'tw-hidden md:tw-block': index > 0 })}
                key={index}
              >
                <div className="tw-p-8 tw-bg-brand-navy tw-rounded-lg tw-border tw-border-brand-green tw-text-center">
                  <div className="tw-text-sm tw-text-white tw-font-semibold tw-text-center tw-mb-4 tw-leading-none">
                    {review.reviewer}
                  </div>

                  <div className="tw-text-center tw-mb-4">
                    {[...Array(review.rating)].map((key) => (
                      <BsStarFill
                        key={key}
                        width={20}
                        height={20}
                        className="tw-text-orange tw-align-top tw-mx-1"
                      />
                    ))}
                  </div>

                  {review.images.length > 0 ? (
                    <div className="tw-flex tw-justify-center tw--mx-1 tw-mb-4 md:tw-mb-6">
                      {review.images.slice(0, 3).map((image, index) => (
                        <div className="tw-px-1" key={image.id}>
                          <ImageWithFallback
                            record={image}
                            imgField="webpSmUrl"
                            alt={image.caption || ''}
                            fill
                            className="tw-static tw-block tw-object-cover tw-rounded-lg tw-w-full tw-h-24 tw-border tw-border-brand-green tw-border-solid tw-pointer-events-none"
                          />
                        </div>
                      ))}
                    </div>
                  ) : null}

                  <div className="tw-text-white tw-text-sm md:tw-text-base tw-h-28 md:tw-h-40 tw-mb-2">
                    <ReadMore
                      mobileLength={140}
                      desktopLength={120}
                      as="div"
                      className=""
                      content={review.strippedBody || ''}
                      dotOnly={true}
                    />

                    {review.body && review.body.length > maxLength ? (
                      <div
                        className="tw-text-center tw-font-semibold tw-underline tw-text-white tw-mt-2 md:tw-mt-3 tw-lowercase tw-cursor-pointer"
                        onClick={() => {
                          setShowMoreReview(review);
                        }}
                      >
                        {translation('showMore')}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {pagination.nextPage ? (
            <>
              <div className="tw-text-center tw-mt-10">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowModal(true)}
                >
                  {translation('viewAllReviews')}
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>

      <Modal
        show={showModal}
        onExit={() => {
          setShowMoreReview(undefined);
        }}
        className="modal-styled"
      >
        <Modal.Header>
          <div className="tw-font-semibold tw-font-styleSans tw-text-xl md:tw-text-2xl tw-leading-none md:tw-leading-none">
            {translation('whatOtherGuestsSay')}
          </div>
          <FiX size={24} className="tw-cursor-pointer" onClick={() => setShowModal(false)} />
        </Modal.Header>
        <Modal.Body>
          {optionReviewsCount > 0 ? (
            <div className="tw-flex tw-items-center tw-border-b tw-border-grey-lighter">
              <div
                onClick={() => {
                  if (reviewTab !== 'option') {
                    setReviewTab('option');
                    setPage(1);
                  }
                }}
                className={classNames(
                  'tw-cursor-pointer tw-border-b-2 tw-font-semibold  tw-font-styleSans tw-text-brand-navy tw-text-lg md:tw-text-xl tw-leading-none md:tw-leading-none tw-px-4 tw-py-3',
                  {
                    'tw-border-orange': reviewTab === 'option',
                    'tw-border-transparent': reviewTab !== 'option',
                  }
                )}
              >
                {translation('activityReviews')} ({optionReviewsCount})
              </div>
              <div
                onClick={() => {
                  if (reviewTab !== 'supplier') {
                    setReviewTab('supplier');
                    setPage(1);
                  }
                }}
                className={classNames(
                  'tw-cursor-pointer tw-border-b-2 tw-font-semibold tw-font-styleSans tw-text-brand-navy tw-text-lg md:tw-text-xl tw-leading-none md:tw-leading-none tw-px-4 tw-py-3',
                  {
                    'tw-border-orange': reviewTab === 'supplier',
                    'tw-border-transparent': reviewTab !== 'supplier',
                  }
                )}
              >
                {translation('hostReviews')} ({supplier.ratedReviewsCount})
              </div>
            </div>
          ) : null}

          <div className="tw-flex tw-items-center tw-mt-3 md:tw-mt-4 tw-mb-6 md:tw-mb-8 tw-text-sm md:tw-text-base">
            <div className="tw-flex tw-items-center">
              <div className="tw-font-semibold tw-mr-2">{translation('sortBy')}:</div>
              <div
                ref={selectorRef}
                className="tw-max-w-max tw-flex tw-justify-between tw-items-center tw-leading-none tw-cursor-pointer"
                onClick={() => setShowSortTypeSelector(true)}
              >
                <div>{translation(`${REVIEW_SORT.find((o) => o.value === reviewSort)?.translationKey}`)}</div>
                <FiChevronDown size={20} />
              </div>
            </div>
            <Overlay
              rootClose={true}
              target={selectorRef}
              show={showSortTypeSelector}
              placement="bottom"
              onHide={() => {
                setShowSortTypeSelector(false);
              }}
              popperConfig={{
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [0, 4],
                    },
                  },
                ],
              }}
            >
              <Popover
                id="sort-type-selector"
                className={`tw-w-60 tw-py-3 tw-px-0 popover-no-arrow`}
              >
                <div className="md:tw-max-h-sm tw-overflow-y-auto tw-relative">
                  <Popover.Content className="tw-px-3">
                    {REVIEW_SORT.map((obj, index) => (
                      <div
                        key={obj.value}
                        onClick={() => {
                          if (containerRef.current) {
                            containerRef.current.scrollTo({ top: 0 });
                          }
                          setReviewSort(obj.value);
                          setPage(1);
                          setShowSortTypeSelector(false);
                        }}
                        className={classNames(
                          'tw-font-semibold tw-font-styleSans md:tw-text-lg tw-text-center tw-cursor-pointer',
                          {
                            'tw-text-brand-navy': obj.value === reviewSort,
                            'tw-text-grey hover:tw-text-brand-navy': obj.value !== reviewSort,
                            'tw-mt-4': index > 0,
                          }
                        )}
                      >
                        {translation(obj.translationKey)}
                      </div>
                    ))}
                  </Popover.Content>
                </div>
              </Popover>
            </Overlay>
          </div>

          <div
            className={`tw-relative tw-overflow-y-auto tw-overflow-x-hidden tw-pr-1 ${style.reviewContainer}`}
            ref={containerRef}
          >
            {reviews && reviews.length > 0
              ? reviews.map((review) => (
                  <Review
                    review={review}
                    key={review.id}
                    showOption={reviewTab === 'supplier'}
                    option={option}
                    onClickImage={(image) => {
                      setShowLightBox(true);
                      setSlideId(`review-lightbox-${image.id}`);
                    }}
                    readMoreClass="tw-block tw-text-black tw-underline tw-cursor-pointer tw-text-brand-navy tw-font-semibold tw-font-styleSans md:tw-text-base tw-mt-2"
                  />
                ))
              : null}
          </div>
        </Modal.Body>
      </Modal>

      <LightBox
        onClose={() => setShowLightBox(false)}
        show={showLightBox}
        items={lightBoxItems}
        enableNav={true}
        slideId={slideId}
      />
    </>
  ) : null;
};

export default ReviewSectionV2;
